if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item']
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev')
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction'
          },
          threshold: 2
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal'
            }
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          }
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap
              }
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5
              },
              1536: {
                slidesPerView: 7
              }
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
        // initSlider() creates the thumbnails with the viewer: destroy them together, or the next
        // initSlider() stacks a second Swiper on the same thumbnails.
        if (typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy();
          this.thumbsInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      // One PhotoSwipe dataSource entry for a given `.product__media-item`.
      buildZoomEntry(media) {
        const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

        let source = {
          id: mediaIndex,
          mediaType: mediaType
        };

        switch (mediaType) {
          case 'model':
          case 'video':
          case 'external_video':
            const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

            source = {
              ...source,
              html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`
            };
            break;
          default: // Image.
            source = {
              ...source,
              src: src,
              width: pswpWidth,
              height: pswpHeight
            };
            break;
        }

        return source;
      }

      initImageZoom() {
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        const dataSource = allMedia.map((media) => this.buildZoomEntry(media));
        this.zoomDataSource = dataSource;

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          // Don't restore focus to the opener thumbnail on close. The opener lives
          // inside the slide it was opened from, so when the tab is re-activated the
          // browser re-focuses (and scrolls) that element into view, hijacking the
          // swiper back to the opening index. Keeping focus off it prevents the jump.
          returnFocus: false
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          return this.querySelector(`.product__media-item[data-media-index="${id}"] img`) || thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          const { currIndex } = this.lightbox?.pswp;
          const slider = this.sliderInstance?.slider;
          if (slider) {
            slider.slideTo(currIndex, 0, false);
          }
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox?.pswp;
          if (!this.sliderInstance && currIndex !== null) {
            const target = this.elements.mediaItems?.find((item) => Number(item.dataset.mediaIndex) === currIndex);
            if (target) {
              requestAnimationFrame(() => {
                const rect = target.getBoundingClientRect();
                const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
                if (!inView) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              });
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: document.documentElement.lang.startsWith('bg') ? 'Следващ слайд' : 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              }
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: document.documentElement.lang.startsWith('bg') ? 'Предишен слайд' : 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              }
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            }
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          }
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image'
          };
        }

        if (!featuredMedia) return;

        if (this.sliderInstance.slider) {
          const mediaItems = this.elements?.mediaItems?.length
            ? this.elements.mediaItems
            : Array.from(this.querySelectorAll(this.selectors.mediaItems[0]));
          const slideIndex = mediaItems.findIndex(
            (media) => String(media.dataset.mediaId) === String(featuredMedia.id)
          );

          if (slideIndex >= 0) {
            this.sliderInstance.slider.slideTo(slideIndex, 0, false);
            return;
          }

          const fallbackIndex = (featuredMedia.position || 1) - 1;
          if (fallbackIndex >= 0) {
            this.sliderInstance.slider.slideTo(fallbackIndex, 0, false);
          }
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image'
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
